import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Briefcase, Clock, CheckCircle, LogOut, Key, FileText, Search, ArrowUpDown } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAutoLogout } from '../hooks/useAutoLogout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TecnicoDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useAutoLogout(); // Enable auto-logout after 5 minutes of inactivity
  const [trabajos, setTrabajos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(null); // null, 'pendiente', 'en_progreso', 'completado'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadTrabajos();
  }, []);

  const loadTrabajos = async () => {
    try {
      setLoading(true);
      // Solo cargar trabajos asignados al técnico
      const response = await axios.get(`${API}/trabajos`, {
        params: { tecnico_id: user.id }
      });
      
      setTrabajos(response.data);
    } catch (err) {
      setError('Error al cargar trabajos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completado':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pendiente':
        return <Clock className="h-4 w-4" />;
      case 'en_progreso':
        return <Briefcase className="h-4 w-4" />;
      case 'completado':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const formatEstado = (estado) => {
    const estados = {
      pendiente: 'Pendiente',
      en_progreso: 'En Progreso',
      completado: 'Completado'
    };
    return estados[estado] || estado;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stats = {
    pendientes: trabajos.filter(t => t.estado === 'pendiente').length,
    en_progreso: trabajos.filter(t => t.estado === 'en_progreso').length,
    completados: trabajos.filter(t => t.estado === 'completado').length
  };

  // Filtrar y ordenar trabajos
  const handleClickEstado = (estado) => {
    setFiltroEstado(filtroEstado === estado ? null : estado);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const trabajosFiltrados = trabajos
    .filter(t => filtroEstado ? t.estado === filtroEstado : true)
    .filter(t =>
      t.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.cliente_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.lugar_nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.ciudad_nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'fecha_creacion') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6" data-testid="tecnico-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/surnexa-logo.png" 
                alt="Surnexa Logo" 
                className="w-32 h-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
                  Panel de Técnico
                </h1>
                <p className="text-gray-600 mt-1">Bienvenido, {user?.nombre_completo}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/cambiar-contrasena')}
                data-testid="change-password-button"
              >
                <Key className="mr-2 h-4 w-4" />
                Cambiar Clave
              </Button>
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Salir
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${filtroEstado === 'pendiente' ? 'bg-yellow-100 border-yellow-400 shadow-lg' : 'bg-yellow-50 border-yellow-200 hover:shadow-md'}`}
            onClick={() => handleClickEstado('pendiente')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-800">Pendientes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-900">{stats.pendientes}</div>
              <p className="text-xs text-yellow-700 mt-1">Click para filtrar</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${filtroEstado === 'en_progreso' ? 'bg-blue-100 border-blue-400 shadow-lg' : 'bg-blue-50 border-blue-200 hover:shadow-md'}`}
            onClick={() => handleClickEstado('en_progreso')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-800">En Progreso</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{stats.en_progreso}</div>
              <p className="text-xs text-blue-700 mt-1">Click para filtrar</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${filtroEstado === 'completado' ? 'bg-green-100 border-green-400 shadow-lg' : 'bg-green-50 border-green-200 hover:shadow-md'}`}
            onClick={() => handleClickEstado('completado')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-green-800">Completados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900">{stats.completados}</div>
              <p className="text-xs text-green-700 mt-1">Click para filtrar</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="mb-6 flex justify-end">
          <Button
            onClick={() => navigate('/tecnico/informes')}
            size="lg"
            variant="outline"
          >
            <FileText className="mr-2 h-5 w-5" />
            Informes
          </Button>
        </div>

        {/* Trabajos List */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {filtroEstado 
              ? `Trabajos ${formatEstado(filtroEstado)}` 
              : 'Mis Trabajos Asignados'}
            {filtroEstado && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFiltroEstado(null)}
                className="ml-3"
              >
                Ver todos
              </Button>
            )}
          </h2>

          {/* Buscador */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por trabajo, cliente, lugar o ciudad..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Cargando trabajos...</p>
              </CardContent>
            </Card>
          ) : trabajosFiltrados.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">
                  {searchTerm 
                    ? 'No se encontraron trabajos' 
                    : filtroEstado 
                      ? `No tienes trabajos ${formatEstado(filtroEstado).toLowerCase()}`
                      : 'No tienes trabajos asignados'}
                </p>
                {(filtroEstado || searchTerm) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFiltroEstado(null);
                      setSearchTerm('');
                    }}
                    className="mt-4"
                  >
                    Ver todos los trabajos
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('titulo')}
                    >
                      <div className="flex items-center gap-2">
                        Trabajo
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('cliente_nombre')}
                    >
                      <div className="flex items-center gap-2">
                        Cliente
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('lugar_nombre')}
                    >
                      <div className="flex items-center gap-2">
                        Lugar
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('ciudad_nombre')}
                    >
                      <div className="flex items-center gap-2">
                        Ciudad
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('estado')}
                    >
                      <div className="flex items-center gap-2">
                        Estado
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('fecha_creacion')}
                    >
                      <div className="flex items-center gap-2">
                        Fecha
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fotos
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trabajosFiltrados.map((trabajo) => (
                    <tr
                      key={trabajo.id}
                      onClick={() => navigate(`/tecnico/trabajo/${trabajo.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`trabajo-row-${trabajo.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{trabajo.titulo}</div>
                          <div className="text-sm text-gray-500 line-clamp-1">{trabajo.descripcion}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {trabajo.cliente_nombre || 'No asignado'}
                        </div>
                        {trabajo.direccion && (
                          <div className="text-xs text-gray-500">{trabajo.direccion}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {trabajo.lugar_nombre || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {trabajo.ciudad_nombre || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${getEstadoColor(trabajo.estado)} flex items-center gap-1 w-fit`}>
                          {getEstadoIcon(trabajo.estado)}
                          {formatEstado(trabajo.estado)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatFecha(trabajo.fecha_creacion)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {trabajo.fotos && trabajo.fotos.length > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {trabajo.fotos.length} foto{trabajo.fotos.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400">Sin fotos</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TecnicoDashboard;
